// The ⓘ that sits beside a dashboard panel title, and the three pieces its
// content is built from.
//
// One component rather than a tooltip per card, because the point of these is
// that a reader learns the affordance once: same icon, same place, same shape
// of answer. Two cards explaining themselves in two different visual languages
// is worse than one of them not explaining itself at all.
//
// Styled as a light card instead of MUI's default dark slab so it reads as
// part of the dashboard's white Paper panels rather than as a system hint.
import React from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const TEAL = '#29C0BB';
const INK = '#374151';
const MUTED = '#6B7280';
const HAIRLINE = '#E5E7EB';

/** A paragraph inside a tooltip. */
export const InfoText = ({ children, strong = false, sx }) => (
  <Typography
    variant="caption"
    sx={{ display: 'block', lineHeight: 1.55, color: strong ? INK : MUTED, fontWeight: strong ? 700 : 400, ...sx }}
  >
    {children}
  </Typography>
);

/** The heading line every tooltip opens with. */
export const InfoHeading = ({ children }) => (
  <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: INK, mb: 0.75 }}>
    {children}
  </Typography>
);

/**
 * One "term — meaning" row. `color` draws the dot that ties the row to its
 * slice or bar; omit it for a definition that has no colour on the chart.
 */
export const InfoRow = ({ term, color, children }) => (
  <Stack direction="row" spacing={0.75} alignItems="flex-start">
    {color && (
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, mt: '5px', flexShrink: 0 }} />
    )}
    <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.55, color: MUTED }}>
      <Box component="span" sx={{ fontWeight: 700, color: INK }}>{term}</Box>
      {' — '}
      {children}
    </Typography>
  </Stack>
);

export default function DashboardInfoTooltip({ label, heading, trackId, children }) {
  return (
    <Tooltip
      arrow
      placement="top"
      // The body runs to several lines, so it has to survive the pointer
      // crossing into it, and a tap has to be enough on a touch screen.
      enterTouchDelay={0}
      leaveTouchDelay={10000}
      title={
        <Box sx={{ p: 1.5 }}>
          {heading && <InfoHeading>{heading}</InfoHeading>}
          {children}
        </Box>
      }
      slotProps={{
        tooltip: {
          sx: {
            p: 0,
            maxWidth: 320,
            backgroundColor: '#FFFFFF',
            color: INK,
            border: `1px solid ${HAIRLINE}`,
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(16, 24, 40, 0.12)',
          },
        },
        arrow: { sx: { color: '#FFFFFF' } },
      }}
    >
      <IconButton
        size="small"
        aria-label={label}
        data-track-id={trackId}
        sx={{ color: TEAL, p: 0.25, '&:hover': { backgroundColor: 'rgba(41,192,187,0.10)' } }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );
}
